// [SUPERADMIN-1-STATS] - Module Super Admin - Connexion
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Eye, EyeOff, Loader2, Lock, Mail, Shield } from "lucide-react";
import { Button } from "../components/ui/button";

// [SUPERADMIN] Identifiants hardcodés - modifiables directement ici
const SUPER_ADMIN = {
  email: "admin@boutiqueos.com",
  password: "SuperAdmin2026!"
};

export { SUPER_ADMIN };

export default function SuperAdminLogin() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [shake, setShake] = useState(false);

  useEffect(() => {
    // Vérifier si déjà connecté
    const session = sessionStorage.getItem("superAdminSession");
    if (session) {
      navigate("/super-admin/dashboard");
    }
  }, [navigate]);

  const triggerShake = () => {
    setShake(true);
    setTimeout(() => setShake(false), 400);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!email || !password) {
      setError("Veuillez remplir tous les champs");
      triggerShake();
      return;
    }

    setLoading(true);

    // Simuler un délai de vérification
    await new Promise(resolve => setTimeout(resolve, 800));

    if (email.toLowerCase() === SUPER_ADMIN.email && password === SUPER_ADMIN.password) {
      // Connexion réussie
      sessionStorage.setItem("superAdminSession", JSON.stringify({
        email: SUPER_ADMIN.email,
        loginTime: new Date().toISOString()
      }));
      toast.success("Bienvenue Super Admin !");
      navigate("/super-admin/dashboard");
    } else {
      setError("Identifiants incorrects");
      triggerShake();
    }

    setLoading(false);
  };

  return (
    <div 
      className="min-h-screen flex items-center justify-center p-4"
      style={{ backgroundColor: '#F8FAFC' }}
      data-testid="super-admin-login"
    >
      <motion.div
        animate={shake ? { x: [-8, 8, -8, 8, 0] } : {}}
        transition={{ duration: 0.4 }}
        className="w-full max-w-[400px] rounded-[20px] p-8"
        style={{ 
          backgroundColor: '#FFFFFF',
          boxShadow: '0 25px 50px rgba(0,0,0,0.1)',
          border: '1px solid #E2E8F0'
        }}
      >
        {/* Logo */}
        <div className="flex flex-col items-center mb-6">
          <div 
            className="w-16 h-16 rounded-2xl flex items-center justify-center mb-3"
            style={{ backgroundColor: '#FF450015' }}
          >
            <Shield className="w-8 h-8" style={{ color: '#FF4500' }} />
          </div>
          <h1 className="font-bold text-xl" style={{ color: '#1E293B' }}>BoutiqueOS</h1>
          <span 
            className="px-3 py-1 rounded-full text-xs font-semibold mt-2"
            style={{ backgroundColor: '#FF4500', color: 'white' }}
          >
            Super Admin
          </span>
        </div>

        {/* Sous-titre */}
        <p className="text-sm text-center mb-7" style={{ color: '#64748B' }}>
          Accès réservé aux administrateurs de la plateforme
        </p>

        {/* Formulaire */}
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Email */}
          <div>
            <div className="relative">
              <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5" style={{ color: '#94A3B8' }} />
              <input
                type="email"
                value={email}
                onChange={(e) => { setEmail(e.target.value); setError(""); }}
                placeholder="admin@boutiqueos.com"
                className="w-full rounded-xl pl-12 pr-4 py-3.5 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-orange-500/20 transition-colors"
                style={{ 
                  backgroundColor: '#F8FAFC', 
                  border: '1px solid #E2E8F0',
                  color: '#1E293B'
                }}
                data-testid="superadmin-email"
              />
            </div>
          </div>

          {/* Mot de passe */}
          <div>
            <div className="relative">
              <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5" style={{ color: '#94A3B8' }} />
              <input
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => { setPassword(e.target.value); setError(""); }}
                placeholder="Mot de passe"
                className="w-full rounded-xl pl-12 pr-12 py-3.5 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-orange-500/20 transition-colors"
                style={{ 
                  backgroundColor: '#F8FAFC', 
                  border: '1px solid #E2E8F0',
                  color: '#1E293B'
                }}
                data-testid="superadmin-password"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-4 top-1/2 -translate-y-1/2 transition-colors"
                style={{ color: '#94A3B8' }}
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>

          {/* Message d'erreur */}
          {error && (
            <motion.p
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-sm text-center"
              style={{ color: '#EF4444' }}
            >
              {error}
            </motion.p>
          )}

          {/* Bouton connexion */}
          <Button
            type="submit"
            disabled={loading}
            className="w-full py-4 rounded-xl font-semibold text-white"
            style={{ backgroundColor: '#FF4500' }}
            data-testid="superadmin-submit"
          >
            {loading ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              "Accéder au tableau de bord"
            )}
          </Button>
        </form>

        {/* Lien retour */}
        <p className="text-center mt-6">
          <button
            onClick={() => navigate("/")}
            className="text-sm transition-colors hover:text-orange-500"
            style={{ color: '#64748B' }}
          >
            Retour à l'accueil
          </button>
        </p>
      </motion.div>
    </div>
  );
}
